// eslint-disable-next-line no-restricted-imports
import { useEffect, useState } from 'react';
import { Box } from '@applyboard/crystal-ui';
import { useEventTracker } from '@applyboard/ui-utils';
import {
  useGetPrefillData,
  useGetSource,
  useGetUtmSource,
} from '../../../common/hooks/useQueryParams';
import useStepper from '../../../common/hooks/useStepper';
import TimelineStepper from '../TimelineStepper';
import { LoanData, LoansFormEvents } from '../types';
import Confirmation from './confirmation';
import Destination from './destination';
import DocumentUpload from './documents';
import EducationSummary from './educationSummary';
import PersonalInformationLead from './personalInformationLead';

export interface StepperState {
  loanData: LoanData;
  setLoanData: (loanData: LoanData) => void;
  goNext: () => void;
  goBack: () => void;
}

export interface LoanFormStepperProps {
  loanData: LoanData;
}

export interface StepDefinition {
  name: string;
  component: (param: StepperState) => JSX.Element;
}

const LOAN_STEPS_SUPPORTING_PARTIAL_APPLICATION = [
  { name: 'Personal Information', component: PersonalInformationLead },
  { name: 'Upload Supporting Documents', component: DocumentUpload },
  { name: 'Destination Information', component: Destination },
  { name: 'Education Summary', component: EducationSummary },
  { name: 'Confirmation', component: Confirmation },
];

const LoanFormStepper = ({ loanData: loanDataInput }: LoanFormStepperProps) => {
  const loanSteps = LOAN_STEPS_SUPPORTING_PARTIAL_APPLICATION;
  const rawPrefillData = useGetPrefillData([
    'nationality',
    'destination',
    'school_name',
    'program_name',
    'field_of_study',
  ]);

  // Merge prefill data into loanData at initialization
  const mergedLoanData: LoanData = {
    ...loanDataInput,
    student: {
      ...loanDataInput.student,
      nationality: loanDataInput.student.nationality || rawPrefillData.nationality || '',
    },
    destinationLeadDetails: {
      destination:
        loanDataInput.destinationLeadDetails?.destination || rawPrefillData.destination || '',
      schoolName:
        loanDataInput.destinationLeadDetails?.schoolName || rawPrefillData.school_name || '',
      programName:
        loanDataInput.destinationLeadDetails?.programName || rawPrefillData.program_name || '',
      fieldOfStudy:
        loanDataInput.destinationLeadDetails?.fieldOfStudy || rawPrefillData.field_of_study || '',
    },
    destinationDetails: loanDataInput.destinationDetails,
  };

  const [loanData, setLoanData] = useState(mergedLoanData);
  const { currentStep, goNext, goBack } = useStepper(loanSteps.length);
  const CurrentStepComponent = loanSteps[currentStep].component;
  const shouldShowTimeline = currentStep === 0 || currentStep === loanSteps.length - 1;
  const { trackEvent } = useEventTracker();
  const utmSource = useGetUtmSource();
  const source = useGetSource();

  useEffect(() => {
    trackEvent({
      event: LoansFormEvents.PageView,
      data: { rp_id: loanDataInput.recruitmentPartnerId, utm_source: utmSource, source: source },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ width: '100%' }}>
      <Box>
        {!shouldShowTimeline && (
          <>
            <TimelineStepper stepDefinitions={loanSteps.slice(0, -1)} currentStep={currentStep} />
            <br />
          </>
        )}
      </Box>
      <CurrentStepComponent
        loanData={loanData}
        setLoanData={setLoanData}
        goNext={goNext}
        goBack={goBack}
      />
    </div>
  );
};

export default LoanFormStepper;
